import React, { createContext, useEffect, useReducer, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { Button, DetailPageHeader, ErrorComponent, Spinner, Tab, Typography, MessageDialog, AdminNavigation } from '../../components';
import { META_TITLE } from '../../../constants/general.constants';
import { fetchUser, removeUser } from '../../services/users';
import { OverviewTab } from './OverviewTab';
import { TemplatesTab } from './TemplatesTab';
import * as S from './AdminAdminDetail.styles';
import { errorHandler } from '../../services/authService';
import { useQuery } from '../../hooks/useQuery';

export const AdminAdminDetailContext = createContext();

const reducer = (state, action) => {
	switch (action.type) {
		case 'SET_USER':
			return {
				...state,
				user: action.payload,
			};
		default:
			return state;
	}
};

const AdminAdminDetail = () => {
	const [pageStatus, setPageStatus] = useState('idle');
	const [state, dispatch] = useReducer(reducer, {});
	const [removeIsOpen, setRemoveIsOpen] = useState(false);
	const { id: userId } = useParams();
	const query = useQuery();

	const history = useHistory();
	const [tabs, setTabs] = useState([
		{
			id: 1,
			label: 'Overview',
			component: <OverviewTab />,
			active: query.get('tabId') === '1',
		},
		{
			id: 2,
			label: 'Templates',
			component: <TemplatesTab />,
			active: query.get('tabId') === '2',
		},
	]);

	const backTitle = location.state?.backTitle || 'Back to Admins';

	function handleOpenRemoveModal() {
		setRemoveIsOpen(true);
	}

	function handleCloseRemoveModal() {
		setRemoveIsOpen(false);
	}

	const handleRemoveUser = async () => {
		try {
			await removeUser({ userId });
			history.push('/admin/admins');
		} catch (error) {
			errorHandler(error);
		}
	};

	async function initializeData() {
		try {
			setPageStatus('loading');
			const {
				data: { result },
			} = await fetchUser({ userId });
			dispatch({ type: 'SET_USER', payload: result.user });
			setPageStatus('success');
		} catch (error) {
			setPageStatus('error');
		}
	}

	function renderContent() {
		switch (pageStatus) {
			case 'loading':
				return <Spinner />;
			case 'success':
				return (
					<AdminAdminDetailContext.Provider value={{ state, dispatch }}>
						<DetailPageHeader title={`${state.user.firstName} ${state.user.lastName}`} backButton={{ label: backTitle, onClick: () => history.push('/admin/admins') }}>
							{state.user.canRemoveUser && (
								<>
									<Button variant="outline" variation="warning" onClick={handleOpenRemoveModal}>
										<Typography weight="bold" variation="button-medium">
											Remove Admin
										</Typography>
									</Button>
									<MessageDialog
										isOpen={removeIsOpen}
										onRequestClose={handleCloseRemoveModal}
										title="Remove Admin?"
										subtitle="Are you sure you want to remove this admin?"
										icon={['fal', 'user']}
										actions={[
											{ id: 'back', label: 'Cancel', variant: 'outline', variation: 'default' },
											{ id: 'cancel', label: 'Remove Admin', variant: 'solid', variation: 'default', onClick: handleRemoveUser },
										]}
									/>
								</>
							)}
						</DetailPageHeader>
						<S.Wrapper>
							<Tab tabs={tabs} setTabs={setTabs} />
						</S.Wrapper>
					</AdminAdminDetailContext.Provider>
				);
			case 'error':
				return <ErrorComponent />;
			default:
				return null;
		}
	}

	useEffect(() => {
		document.title = `Admin User Details | ${META_TITLE}`;
	}, []);

	useEffect(() => {
		if (pageStatus === 'idle') {
			initializeData();
		}
	}, [pageStatus]);

	return <AdminNavigation>{renderContent()}</AdminNavigation>;
};

export default AdminAdminDetail;
